// ---- the transcript, read as tool calls ----------------------------------------
//
// A session's mirror keeps one short line per tool call ("Bash: ls", "Read src/a.ts"):
// no input, no result. The factory keeps the whole stream (`GET /runs/{name}/transcript`,
// Claude's stream-json, one JSON object per line). This reads that stream into calls
// (input, result, error, time) and thinking, and pairs the mirror's tool lines with
// them in order, so a step in the thread can be opened to its full detail.

export interface Call {
  id: string
  name: string
  input: unknown
  /** the tool's answer as text; undefined while it is still running */
  result?: string
  error?: boolean
  /** ISO time the call was made, when the stream carries one */
  at?: string
  /** how long it took, when both ends carry a time */
  ms?: number
  /** the model's thinking since the call before this one */
  thinking?: string
}

export interface Transcript {
  calls: Call[]
  /** thinking that came before an assistant message, by that message's trimmed text */
  thoughtsBefore: Map<string, string>
}

const obj = (v: unknown): Record<string, unknown> | undefined =>
  v && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : undefined

const timeOf = (o: Record<string, unknown>): string | undefined => {
  for (const k of ['timestamp', 'ts', 'at', 'time']) {
    const v = o[k]
    if (typeof v === 'string' && !Number.isNaN(Date.parse(v))) return v
  }
  return undefined
}

/** A tool_result's content is text, or blocks of it (and pictures, named as such). */
const resultText = (content: unknown): string => {
  if (typeof content === 'string') return content
  if (Array.isArray(content))
    return content
      .map((b) => {
        const o = obj(b)
        if (!o) return typeof b === 'string' ? b : ''
        if (typeof o.text === 'string') return o.text
        return o.type === 'image' ? '[picture]' : JSON.stringify(o)
      })
      .join('\n')
  return content == null ? '' : JSON.stringify(content)
}

/** Reads the stream's lines. A line that is not JSON (or is a stream event of no interest) is skipped. */
export const parseTranscript = (lines: string[]): Transcript => {
  const calls: Call[] = []
  const byId = new Map<string, Call>()
  const thoughtsBefore = new Map<string, string>()
  let thinking = ''
  for (const raw of lines) {
    let line: Record<string, unknown> | undefined
    try {
      line = obj(JSON.parse(raw.replace(/^data:\s*/, '')))
    } catch {
      continue
    }
    if (!line) continue
    const at = timeOf(line)
    const msg = obj(line.message)
    const content = msg?.content
    if (!Array.isArray(content)) continue
    for (const b of content) {
      const block = obj(b)
      if (!block) continue
      if (line.type === 'assistant') {
        if (block.type === 'thinking' && typeof block.thinking === 'string') {
          thinking = thinking ? `${thinking}\n\n${block.thinking}` : block.thinking
        } else if (block.type === 'text' && typeof block.text === 'string') {
          if (thinking) thoughtsBefore.set(block.text.trim(), thinking)
          thinking = ''
        } else if (block.type === 'tool_use' && typeof block.id === 'string') {
          const call: Call = {
            id: block.id,
            name: String(block.name ?? ''),
            input: block.input,
            at,
            thinking: thinking || undefined,
          }
          thinking = ''
          calls.push(call)
          byId.set(call.id, call)
        }
      } else if (line.type === 'user' && block.type === 'tool_result') {
        const call = byId.get(String(block.tool_use_id ?? ''))
        if (!call) continue
        call.result = resultText(block.content)
        call.error = block.is_error === true
        if (at && call.at) {
          const ms = Date.parse(at) - Date.parse(call.at)
          if (ms >= 0) call.ms = ms
        }
      }
    }
  }
  return { calls, thoughtsBefore }
}

/** The text values of an input, for finding which call a mirror line was about. */
const inputText = (input: unknown): string => {
  const o = obj(input)
  if (!o) return typeof input === 'string' ? input : ''
  return Object.values(o)
    .map((v) => (typeof v === 'string' ? v : JSON.stringify(v)))
    .join('\n')
}

/** The tool a mirror line names, as the stream names it. */
const lineTool = (line: string): { name: string; detail: string } => {
  const text = line.trim()
  if (text === 'Updated its plan') return { name: 'TodoWrite', detail: '' }
  const bash = /^Bash:\s*(.*)$/s.exec(text)
  if (bash) return { name: 'Bash', detail: bash[1] ?? '' }
  const sp = text.indexOf(' ')
  return { name: sp < 0 ? text : text.slice(0, sp), detail: sp < 0 ? '' : text.slice(sp + 1).trim() }
}

/**
 * Pairs the mirror's tool lines (in order) with the stream's calls: the next call
 * of that tool whose input holds the line's detail, else the next of that tool
 * within a few calls. A line with no match (older than the stream, say) gets none.
 */
export const matchLines = (lines: string[], calls: Call[]): (Call | undefined)[] => {
  let cursor = 0
  return lines.map((line) => {
    const { name, detail } = lineTool(line)
    const same = (c: Call) => c.name === name
    const probe = detail.replace(/…$/, '').slice(0, 24)
    let hit = -1
    for (let i = cursor; i < calls.length; i++) {
      const c = calls[i]!
      if (same(c) && (!probe || inputText(c.input).includes(probe))) {
        hit = i
        break
      }
    }
    if (hit < 0) {
      for (let i = cursor; i < Math.min(calls.length, cursor + 4); i++) {
        if (same(calls[i]!)) {
          hit = i
          break
        }
      }
    }
    if (hit < 0) return undefined
    cursor = hit + 1
    return calls[hit]
  })
}

/** Characters shown of a long input or result before "Show all". */
export const TRIM = 3000

/** An input as text to read: a single string as it is, anything else as indented JSON. */
export const inputAsText = (input: unknown): string => {
  if (typeof input === 'string') return input
  if (input === undefined) return ''
  return JSON.stringify(input, null, 2)
}

/** Trims a long text to `max`, saying how much was left out. */
export const trimmed = (text: string, max = TRIM): { shown: string; left: number } =>
  text.length <= max ? { shown: text, left: 0 } : { shown: text.slice(0, max), left: text.length - max }
